// A real league that has not drafted, from the server (ICD-S01 `setupView`),
// and the commissioner's actions on it.
//
// A HOOK RATHER THAN A PROVIDER, unlike `LeaguesProvider`. That one is a
// provider because two things on screen at once read the hub; this is read by
// exactly one screen, so a hook fetches once and nothing has to agree with
// anything.
//
// Both halves went through `data/read.ts` and `data/api.ts` on 2026-09-17.
// What was here before — the abort keyed on the id, `loading` starting true,
// Zod at the boundary, the three outcomes — was right, and that is exactly
// why it moved: four other readers needed the same four rules and each had
// copied a different subset of them.

import { z } from 'zod';
import {
  BotCastView,
  InvitesSent,
  LeagueLineupsView,
  LeagueSetupView,
  PendingInvites,
  type FillSeatsRequest,
  type SeatBotRequest,
  type SkillBandId,
} from '@fsp/domain';
import { send, type Reply } from './api';
import { useRead, type Read } from './read';
import { thenRefresh, useMovesVersion } from './moves';

export function useLeagueSetup(leagueId: string | null): Read<LeagueSetupView | null> {
  const path = leagueId === null ? null : `/leagues/${encodeURIComponent(leagueId)}/setup`;
  // Asked again when this tab changes something in the league from outside the
  // screen — a team renamed from the switcher's Edit my team (SCENARIOS L2) — the same
  // signal every other league read listens to.
  return useRead(path, LeagueSetupView, { refresh: useMovesVersion(leagueId) });
}

/**
 * POST to one of the league's own endpoints.
 *
 * It lives beside the reader because every one of these ends by re-reading the
 * same view: leaving a seat, opening one, closing one, rotating the code and
 * every step of an election all change what the screen is looking at.
 *
 * THE BODY IS NOT PARSED, and that is deliberate rather than lazy. These six
 * endpoints answer three different shapes — `LeagueSetupView` from the seat
 * and invite-code actions, `ElectionView` from the election, and an empty
 * object from leaving and from handing the role over — so there is no one
 * schema to hold them, and the screen re-reads the view afterwards regardless.
 * Declaring one here and quietly discarding whatever did not match would be a
 * silent fallback dressed up as validation; the re-read is what checks the
 * shape, through the same reader as every other read.
 */
export function leagueAction(
  leagueId: string,
  path: string,
  body?: unknown,
): Promise<Reply<unknown>> {
  return send('POST', `/leagues/${encodeURIComponent(leagueId)}${path}`, body, z.unknown());
}

/**
 * The commissioner's seat acts past the draft (MANAGERS.md, J4 and J3): open a
 * seat, make or cancel the link that hands a team to a new manager, and put a
 * bot on a team nobody runs or take it off. Each answers the seats as they now
 * stand, parsed, so the sheet shows the new state at once; and every league
 * read is asked again, because a team's manager is on most of them ("Former
 * manager", the new name, the bot's).
 */
export function seatAct(
  leagueId: string,
  teamId: string,
  act: 'remove' | 'invite' | 'cancel-invite' | 'bot' | 'unbot',
): Promise<Reply<LeagueSetupView>> {
  const seat = `/leagues/${encodeURIComponent(leagueId)}/seats/${encodeURIComponent(teamId)}`;
  const [method, path] =
    act === 'remove'
      ? (['POST', '/open'] as const)
      : act === 'invite'
        ? (['POST', '/invite'] as const)
        : act === 'cancel-invite'
          ? (['DELETE', '/invite'] as const)
          : act === 'bot'
            ? (['POST', '/bot'] as const)
            : (['DELETE', '/bot'] as const);
  return thenRefresh(leagueId, send(method, `${seat}${path}`, undefined, LeagueSetupView));
}

/**
 * Invite some addresses by email (PLAN-2026-10-28-by-email §3.1): who was
 * emailed, who was not and why, and the pending list after it. The league is
 * read again, so the page's list moves with the send.
 */
export function sendInvites(
  leagueId: string,
  addresses: readonly string[],
): Promise<Reply<InvitesSent>> {
  return thenRefresh(
    leagueId,
    send('POST', `/leagues/${encodeURIComponent(leagueId)}/invites`, { addresses }, InvitesSent),
  );
}

/** Take back an invite by email, answering the pending list after it. */
export function takeBackInvite(leagueId: string, inviteId: string): Promise<Reply<PendingInvites>> {
  return thenRefresh(
    leagueId,
    send(
      'DELETE',
      `/leagues/${encodeURIComponent(leagueId)}/invites/${encodeURIComponent(inviteId)}`,
      undefined,
      PendingInvites,
    ),
  );
}

/**
 * Nudge a team's manager about this week's lineup (MANAGERS.md "A quiet
 * manager", J3). Answers every team's lineup at a glance, as it now stands.
 */
export function nudgeLineup(leagueId: string, teamId: string): Promise<Reply<LeagueLineupsView>> {
  return send(
    'POST',
    `/leagues/${encodeURIComponent(leagueId)}/teams/${encodeURIComponent(teamId)}/lineup/nudge`,
    undefined,
    LeagueLineupsView,
  );
}

/**
 * The cast a bot is chosen from (`GET /api/bots`, AI_MANAGERS.md §3.1): read
 * only while a chooser is open, since a page that never chooses never needs it.
 */
export function useBotCast(open: boolean): Read<BotCastView | null> {
  return useRead(open ? '/bots' : null, BotCastView);
}

/**
 * A bot in one seat (AI_MANAGERS.md §3.1, B-459): a character by id, a band,
 * either or neither — neither is the house's pick at its own band.
 */
export function seatBot(
  leagueId: string,
  teamId: string,
  choice: SeatBotRequest,
): Promise<Reply<LeagueSetupView>> {
  return thenRefresh(
    leagueId,
    send(
      'POST',
      `/leagues/${encodeURIComponent(leagueId)}/seats/${encodeURIComponent(teamId)}/bot`,
      choice,
      LeagueSetupView,
    ),
  );
}

/** Every open seat a bot before the draft, playing round `band` (B-459). */
export function fillSeats(leagueId: string, band: SkillBandId): Promise<Reply<LeagueSetupView>> {
  return thenRefresh(
    leagueId,
    send(
      'POST',
      `/leagues/${encodeURIComponent(leagueId)}/seats/bots`,
      { band } satisfies FillSeatsRequest,
      LeagueSetupView,
    ),
  );
}
